import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Home, User, LogOut, Shield, ChevronDown, MessageSquare, Heart, Phone, X, BarChart3, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DailyCheckIn from './DailyCheckIn';

const Avatar = ({ user, className }) => {
  const [failed, setFailed] = useState(false);
  const initials = user.name
    ? user.name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
    : '?';
  const src = user.picture || user.avatar;

  if (failed || !src) {
    return (
      <div className={`${className} bg-purple-500 flex items-center justify-center text-white text-sm font-bold select-none`}>
        {initials}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={user.name}
      className={className}
      onError={() => setFailed(true)}
    />
  );
};

const Navbar = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const desktopDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const handleCheckInComplete = () => {
    setTimeout(() => {
      setShowCheckInModal(false);
    }, 2000);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideDesktop = desktopDropdownRef.current && !desktopDropdownRef.current.contains(event.target);
      const clickedOutsideMobile = mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target);

      if (clickedOutsideDesktop && clickedOutsideMobile) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Top Navigation - Desktop and Mobile */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img
                src="/logo-simby-full.png"
                alt="Simby"
                style={{ width: '150px', height: 'auto' }}
              />
            </Link>

            {user && (
              <>
                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-6">
                  {/* Navigation links */}
                  <div className="flex items-center space-x-2">
                    <Link to="/dashboard">
                      <Button
                        variant={isActive('/dashboard') ? 'default' : 'ghost'}
                        size="sm"
                        className={isActive('/dashboard') ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      >
                        <Home className="w-4 h-4 mr-2" />
                        Início
                      </Button>
                    </Link>
                    {/* "Meu Progresso" oculto (gamificação fora de foco no MVP) */}
                    <Link to="/pdi">
                      <Button
                        variant={isActive('/pdi') ? 'default' : 'ghost'}
                        size="sm"
                        className={isActive('/pdi') ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Plano
                      </Button>
                    </Link>
                    <Link to="/simulators">
                      <Button
                        variant={isActive('/simulators') ? 'default' : 'ghost'}
                        size="sm"
                        className={isActive('/simulators') ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      >
                        🎭
                        <span className="ml-2">Simuladores</span>
                      </Button>
                    </Link>
                  </div>

                  {/* User Profile Dropdown */}
                  <div className="relative" ref={desktopDropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                    >
                      <Avatar user={user} className="w-10 h-10 rounded-full border-2 border-purple-400 hover:border-purple-600 transition-colors" />
                      <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>

                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <User className="w-4 h-4 mr-3" />
                          Perfil
                        </Link>

                        {/* "Meu Círculo" oculto (gamificação fora de foco no MVP) */}

                        <Link
                          to="/emergency-empathy"
                          className="flex items-center px-4 py-2 text-sm text-rose-600 hover:bg-rose-50"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <Phone className="w-4 h-4 mr-3" />
                          Empatia de Emergência
                        </Link>

                        {user.isHrManager && !user.isAdmin && (
                          <>
                            <div className="border-t border-gray-100 my-1"></div>
                            <Link
                              to="/gestor"
                              className="flex items-center px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                              onClick={() => setIsDropdownOpen(false)}
                            >
                              <BarChart3 className="w-4 h-4 mr-3" />
                              Relatorios do Grupo
                            </Link>
                          </>
                        )}

                        {user.isAdmin && (
                          <>
                            <div className="border-t border-gray-100 my-1"></div>
                            <Link
                              to="/admin"
                              className="flex items-center px-4 py-2 text-sm text-purple-700 hover:bg-purple-50"
                              onClick={() => setIsDropdownOpen(false)}
                            >
                              <Shield className="w-4 h-4 mr-3" />
                              Admin
                            </Link>
                          </>
                        )}

                        <div className="border-t border-gray-100 my-1"></div>

                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <LogOut className="w-4 h-4 mr-3" />
                          Sair
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mobile - Profile Icon Only */}
                <div className="md:hidden relative" ref={mobileDropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                  >
                    <Avatar user={user} className="w-10 h-10 rounded-full border-2 border-purple-400" />
                  </button>

                  {/* Mobile Dropdown */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>

                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <User className="w-4 h-4 mr-3" />
                        Perfil
                      </Link>

                      {/* "Meu Círculo" oculto (gamificação fora de foco no MVP) */}

                      <Link
                        to="/diary"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <Heart className="w-4 h-4 mr-3" />
                        Diário de Gatilhos
                      </Link>

                      <Link
                        to="/emergency-empathy"
                        className="flex items-center px-4 py-2 text-sm text-rose-600 hover:bg-rose-50"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <Phone className="w-4 h-4 mr-3" />
                        Empatia de Emergência
                      </Link>

                      {user.isHrManager && !user.isAdmin && (
                        <>
                          <div className="border-t border-gray-100 my-1"></div>
                          <Link
                            to="/gestor"
                            className="flex items-center px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <BarChart3 className="w-4 h-4 mr-3" />
                            Relatorios do Grupo
                          </Link>
                        </>
                      )}

                      {user.isAdmin && (
                        <>
                          <div className="border-t border-gray-100 my-1"></div>
                          <Link
                            to="/admin"
                            className="flex items-center px-4 py-2 text-sm text-purple-700 hover:bg-purple-50"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <Shield className="w-4 h-4 mr-3" />
                            Admin
                          </Link>
                        </>
                      )}

                      <div className="border-t border-gray-100 my-1"></div>

                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Sair
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Bottom Navigation - Mobile Only */}
      {user && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-lg">
          <div className="flex justify-around items-center h-14">
            <Link
              to="/dashboard"
              className={`flex flex-col items-center justify-center flex-1 py-1.5 ${isActive('/dashboard') ? 'text-purple-600' : 'text-gray-600'
                }`}
            >
              <Home className={`w-5 h-5 ${isActive('/dashboard') ? 'fill-purple-600' : ''}`} />
              <span className="text-[10px] mt-0.5">Início</span>
            </Link>

            {/* "Progresso" oculto (gamificação fora de foco no MVP) */}

            <Link
              to="/pdi"
              className={`flex flex-col items-center justify-center flex-1 py-1.5 ${isActive('/pdi') ? 'text-purple-600' : 'text-gray-600'
                }`}
            >
              <Sparkles className={`w-5 h-5 ${isActive('/pdi') ? 'stroke-purple-600' : ''}`} />
              <span className="text-[10px] mt-0.5">Plano</span>
            </Link>

            <Link
              to="/simulators"
              className={`flex flex-col items-center justify-center flex-1 py-1.5 ${isActive('/simulators') ? 'text-purple-600' : 'text-gray-600'
                }`}
            >
              <MessageSquare className={`w-5 h-5 ${isActive('/simulators') ? 'fill-purple-600' : ''}`} />
              <span className="text-[10px] mt-0.5">Simuladores</span>
            </Link>
          </div>
        </nav>
      )}

      {/* Add padding to body when mobile nav is visible */}
      {user && <div className="md:hidden h-14"></div>}

      {/* Daily Check-in Modal */}
      {showCheckInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full mx-4 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowCheckInModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>

            <div className="p-8">
              <DailyCheckIn onComplete={handleCheckInComplete} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;

